import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi, type AccessibleTenant, type LoginCredentials, type LoginOutcome, type SessionSnapshot } from '../../shared/api/auth';
import { ApiRequestError, isNetworkFailure, isOfflineUnavailable } from '../../shared/api/http';
import { isOnline, subscribeToConnectivity } from '../../offline/connectivity';
import { emitOffline } from '../../offline/events';
import { setOfflineIdentity } from '../../offline/interceptor';
import { listOutbox } from '../../offline/outbox';
import {
  enrollVault,
  lockVault,
  readVaultSummary,
  refreshVault,
  surrenderDeviceUnlock,
  unlockVault,
  unlockVaultWithDevice,
  type VaultContents,
} from '../../offline/vault';

type AuthPhase =
  | 'loading'
  | 'anonymous'
  | 'authenticated'
  /** No connection, but this device holds an offline copy that a password would open. */
  | 'offline-locked'
  | 'error';

interface AuthContextValue {
  phase: AuthPhase;
  session: SessionSnapshot | null;
  tenants: readonly AccessibleTenant[];
  selectedTenant: AccessibleTenant | null;
  userLabel: string;
  /** True while the session on screen was opened from the device copy rather than by the server. */
  isOfflineSession: boolean;
  login: (credentials: LoginCredentials, options?: { rememberOffline?: boolean }) => Promise<LoginOutcome>;
  completeMfa: (methodId: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  selectTenant: (tenantId: string) => Promise<void>;
  retryHydration: () => Promise<void>;
  /** Signs in against the sealed copy on this device. Resolves to the session it opened. */
  unlockOffline: (password: string) => Promise<VaultContents>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function labelFor(session: SessionSnapshot | null, fallback: string): string {
  return session?.displayName || session?.userName || fallback || session?.userId || '';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<AuthPhase>('loading');
  const [session, setSession] = useState<SessionSnapshot | null>(null);
  const [tenants, setTenants] = useState<readonly AccessibleTenant[]>([]);
  const [verifiedLoginIdentity, setVerifiedLoginIdentity] = useState('');
  const [isOfflineSession, setIsOfflineSession] = useState(false);

  /**
   * The password, held only for as long as it takes a completed sign-in to seal the device copy
   * with it. A password never reaches storage — what is stored is a key wrapped under it — but it
   * has to survive the round trip through a second factor, which is what this is for.
   */
  const offlineIntent = useRef<{ password: string; remember: boolean } | null>(null);

  /**
   * What was typed into the sign-in form, for the sessions whose display name the server leaves
   * empty. Held in a ref as well as in state so the hydration callback does not change identity
   * every time someone signs in — and so mounting does not run hydration twice.
   */
  const loginIdentity = useRef('');

  /**
   * Brings the device copy in step with a sign-in that the server has just confirmed.
   *
   * Enrolling, re-sealing and repairing are all the same moment from the operator's point of view
   * — they signed in — so they are all decided here rather than in three places in the interface.
   */
  const alignOfflineCopy = useCallback(async (nextSession: SessionSnapshot, nextTenants: readonly AccessibleTenant[], label: string) => {
    const intent = offlineIntent.current;
    offlineIntent.current = null;

    try {
      const summary = await readVaultSummary();

      if (summary === null) {
        if (intent?.remember === true) await enrollVault({ password: intent.password, userLabel: label, session: nextSession, tenants: nextTenants });
        return;
      }

      // A different person on a shared device: the previous copy is theirs, and re-enrolling is
      // the only honest thing to do with it.
      if (summary.userId !== nextSession.userId) {
        if (intent?.remember === true) await enrollVault({ password: intent.password, userLabel: label, session: nextSession, tenants: nextTenants });
        return;
      }

      if (intent !== null) {
        try {
          await unlockVault(intent.password);
        } catch {
          // The password no longer opens the copy — it was changed since this device was enrolled.
          // Re-sealing is safe only when nothing is waiting to be sent; unsent writes are sealed
          // under the old key, and discarding them silently would discard someone's work.
          const queue = await listOutbox();
          if (queue.length === 0) {
            await enrollVault({ password: intent.password, userLabel: label, session: nextSession, tenants: nextTenants });
            return;
          }
        }
      } else {
        // A reload of a session the server still accepts. The device key opens the copy so the
        // reader is not asked for a password they have already given today.
        await unlockVaultWithDevice();
      }

      await refreshVault(nextSession, nextTenants, label);
    } catch {
      // The offline copy is an addition to the workspace, never a condition of using it: a device
      // that cannot keep one still signs in and works online.
    }
  }, []);

  const hydrate = useCallback(async () => {
    setPhase('loading');
    try {
      const nextSession = await authApi.me();
      const nextTenants = await authApi.tenants();
      setSession(nextSession);
      setTenants(nextTenants);
      setIsOfflineSession(false);
      setPhase('authenticated');
      setOfflineIdentity({ userId: nextSession.userId, tenantId: nextSession.selectedTenantId });
      await alignOfflineCopy(nextSession, nextTenants, labelFor(nextSession, loginIdentity.current));
      emitOffline('vault-changed');
    } catch (error) {
      setSession(null);
      setTenants([]);
      if (error instanceof ApiRequestError && error.status === 401) {
        setPhase('anonymous');
        return;
      }

      // Nothing was reached. If this device holds a copy that has not expired, the password can
      // still open it, so the reader is offered that rather than an error they cannot act on.
      if (isOfflineUnavailable(error) || isNetworkFailure(error)) {
        const summary = await readVaultSummary();
        if (summary !== null && !summary.expired) {
          setPhase('offline-locked');
          setOfflineIdentity({ userId: summary.userId, tenantId: null });
          return;
        }
      }

      setPhase('error');
    }
  }, [alignOfflineCopy]);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  const login = useCallback(async (credentials: LoginCredentials, options?: { rememberOffline?: boolean }) => {
    offlineIntent.current = { password: credentials.password, remember: options?.rememberOffline === true };
    const outcome = await authApi.login(credentials);
    if (outcome.status === 'succeeded') {
      loginIdentity.current = credentials.email.trim();
      setVerifiedLoginIdentity(credentials.email.trim());
      await hydrate();
    }
    return outcome;
  }, [hydrate]);

  const completeMfa = useCallback(async (methodId: string, code: string) => {
    await authApi.verifyMfa(methodId, code);
    await hydrate();
  }, [hydrate]);

  const unlockOffline = useCallback(async (password: string) => {
    const contents = await unlockVault(password);
    setSession(contents.session);
    setTenants(contents.tenants);
    setIsOfflineSession(true);
    setPhase('authenticated');
    setOfflineIdentity({ userId: contents.session.userId, tenantId: contents.session.selectedTenantId });
    emitOffline('vault-changed');
    return contents;
  }, []);

  const logout = useCallback(async () => {
    try {
      // With no connection there is no server session to end; sealing the copy is the sign-out.
      if (isOnline()) await authApi.logout();
    } finally {
      // The copy stays on the device, but from here only the password opens it again.
      await surrenderDeviceUnlock().catch(() => lockVault());
      emitOffline('vault-changed');
      queryClient.clear();
      loginIdentity.current = '';
      setVerifiedLoginIdentity('');
      setSession(null);
      setTenants([]);
      setIsOfflineSession(false);
      setOfflineIdentity({ userId: null, tenantId: null });
      setPhase('anonymous');
    }
  }, [queryClient]);

  const selectTenant = useCallback(async (tenantId: string) => {
    const nextSession = await authApi.selectTenant(tenantId);
    const nextTenants = await authApi.tenants();
    queryClient.clear();
    setTenants(nextTenants);
    setSession(nextSession);
    setOfflineIdentity({ userId: nextSession.userId, tenantId: nextSession.selectedTenantId });
    await refreshVault(nextSession, nextTenants, labelFor(nextSession, loginIdentity.current)).catch(() => undefined);
  }, [queryClient]);

  /**
   * An offline session is a stand-in for a real one. The moment the server is reachable again it
   * is replaced by what the server actually says — which may be that the session has expired, and
   * that is exactly the answer the operator needs before they keep entering documents.
   */
  useEffect(() => {
    if (!isOfflineSession) return undefined;
    return subscribeToConnectivity(() => {
      if (isOnline()) void hydrate();
    });
  }, [hydrate, isOfflineSession]);

  const selectedTenant = useMemo(
    () => tenants.find((tenant) => tenant.tenantId === session?.selectedTenantId) ?? null,
    [session?.selectedTenantId, tenants],
  );

  const userLabel = labelFor(session, verifiedLoginIdentity);

  const value = useMemo<AuthContextValue>(() => ({
    phase,
    session,
    tenants,
    selectedTenant,
    userLabel,
    isOfflineSession,
    login,
    completeMfa,
    logout,
    selectTenant,
    retryHydration: hydrate,
    unlockOffline,
  }), [completeMfa, hydrate, isOfflineSession, login, logout, phase, selectTenant, selectedTenant, session, tenants, unlockOffline, userLabel]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) throw new Error('useAuth must be used within AuthProvider.');
  return context;
}
